import React from 'react';
import { Collapse, Descriptions } from 'antd';

import { CandidateProjectMatchDetail, Recommendation, Recommendations } from './matching.types';
import { formatMatchingTime, processingReason, stageLabel } from './matchingPresentation';

const coverageLabels: Record<keyof Recommendations<Recommendation>['coverage'], string> = {
  eligiblePairs: 'Eligible pairs',
  discoveredPairs: 'Discovered pairs',
  undiscoveredPairs: 'Not yet discovered',
  currentDetailed: 'Current detailed assessments',
  currentQuickRejected: 'Rejected at initial check',
  outstanding: 'Outstanding',
  blocked: 'Blocked',
  failed: 'Failed',
  retainedStaleRecommendations: 'Retained outdated assessments',
  outsideAutomaticScopePairs: 'Outside automatic scope',
};
export const MatcherProcessingDetails = ({
  data,
}: {
  data: Recommendations<Recommendation> | CandidateProjectMatchDetail;
}) => (
  <Collapse className="matching-technical">
    <Collapse.Panel header="Matcher processing details" key="processing">
      <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 3 }}>
        <Descriptions.Item label="Processing enabled">{data.processingEnabled ? 'Yes' : 'No'}</Descriptions.Item>
        <Descriptions.Item label="Processing active">{data.processingActive ? 'Yes' : 'No'}</Descriptions.Item>
        <Descriptions.Item label="Global blocker">
          {data.processingBlockedReason ? processingReason(data.processingBlockedReason) : 'None'}
        </Descriptions.Item>
        {'coverage' in data ? (
          <>
            {Object.entries(data.coverage).map(([key, value]) => (
              <Descriptions.Item key={key} label={coverageLabels[key as keyof typeof coverageLabels]}>
                {value}
              </Descriptions.Item>
            ))}
            <Descriptions.Item label="Last reconciled">{formatMatchingTime(data.lastReconciledAt)}</Descriptions.Item>
            <Descriptions.Item label="Version checked">
              {formatMatchingTime(data.assessmentManifestCheckedAt)}
            </Descriptions.Item>
          </>
        ) : (
          <>
            <Descriptions.Item label="Publication">{data.publishedOutcome || 'None'}</Descriptions.Item>
            <Descriptions.Item label="Quick check">
              {stageLabel(data.processing.quickStatus, data.processing)}
            </Descriptions.Item>
            <Descriptions.Item label="Detailed assessment">
              {stageLabel(data.processing.detailedStatus, data.processing)}
            </Descriptions.Item>
            <Descriptions.Item label="Refresh pending">
              {data.processing.refreshPending ? 'Yes' : 'No'}
            </Descriptions.Item>
            <Descriptions.Item label="Pair blocker">
              {data.processing.blockedReason ? processingReason(data.processing.blockedReason) : 'None'}
            </Descriptions.Item>
            {data.freshness && (
              <>
                <Descriptions.Item label="Candidate changed">
                  {data.freshness.candidateChanged ? 'Yes' : 'No'}
                </Descriptions.Item>
                <Descriptions.Item label="Project changed">
                  {data.freshness.projectChanged ? 'Yes' : 'No'}
                </Descriptions.Item>
                <Descriptions.Item label="Assessment version changed">
                  {data.freshness.assessmentVersionKnown
                    ? data.freshness.assessmentVersionChanged
                      ? 'Yes'
                      : 'No'
                    : 'Unverified'}
                </Descriptions.Item>
              </>
            )}
            {data.assessment && (
              <>
                <Descriptions.Item label="Score version">{data.assessment.scoreVersion}</Descriptions.Item>
                <Descriptions.Item label="Evidence reference date">{data.assessment.referenceDate}</Descriptions.Item>
              </>
            )}
          </>
        )}
      </Descriptions>
    </Collapse.Panel>
  </Collapse>
);
